import {
  Component,
  HostListener,
  Inject,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FileItem, Order, User } from '@authority-labs/api-interfaces';
import { LIMIT, PERMISSION_KEYS } from '@authority-labs/constants';
import {
  InspectionTypeEnum,
  OrderStatusEnum,
  Path
} from '@authority-labs/enums';
import { UploadDirectory, UploadFile } from '@check-list/app/classes';
import { ComponentCanDeactivate } from '@check-list/app/classes/component-can-deactivate';
import { InvoiceData } from '@check-list/app/classes/models/invoice-data.model';
import { OrdersService } from '@check-list/app/modules/orders/orders.service';
import { UsersService } from '@check-list/app/modules/users/users.service';
import { environment } from '@check-list/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CommonService } from '../../services/common/common.service';
import { FilesService } from '../../services/files/files.service';
import { NzModalConfirmActionComponent } from '../confirm-action-dialog/confirm-action-dialog.component';

@Component({
  selector: 'authority-labs-certificate-dialog',
  templateUrl: './certificate-dialog.component.html',
  styleUrls: ['./certificate-dialog.component.scss']
})
export class CertificateDialogComponent extends ComponentCanDeactivate {
  @ViewChild('orderVersionTemplate')
  orderVersionTemplate: TemplateRef<unknown>;

  isDirty = false;
  order: Order;
  submitting = false;
  uploadingCertificate = false;
  version = 0;
  assignee: User;
  approval: User;
  editPermissions = [PERMISSION_KEYS.CERTIFICATES_EDIT];
  adminPermissions = [PERMISSION_KEYS.CERTIFICATES_ADMIN];

  apiUrl = environment.API;

  get orderVersionId(): string {
    return this.order.version
      ? `${this.order.id}-${this.order.version}`
      : String(this.order.id);
  }

  get title(): string {
    return this.order
      ? `${this.orderVersionId} ${this.order.subProject?.name || ''}`
      : '';
  }

  get isFixedBefore(): boolean {
    return this.order?.version > 0;
  }

  get isFixingOrder(): boolean {
    const isFixingOrder =
      this.order?.status?.key === OrderStatusEnum.FixingOrder;

    return isFixingOrder;
  }

  get isClosedOrder(): boolean {
    return this.order?.status?.key === OrderStatusEnum.Closed;
  }

  get isApproved(): boolean {
    return this.order?.isApproved;
  }

  get canDeleteCertificate(): boolean {
    if (this.isClosedOrder) return false;
    if (this.order?.version > 0) return false;
    const certId = this.order?.sharePoint?.certificateId;
    if (!certId || Object.keys(certId).length === 0) return false;
    if (Object.keys(certId).length > 1) return false;
    return true;
  }

  get versions(): number[] {
    let arr: number[] = [];

    if (this.order?.sharePoint?.certificateId) {
      arr = Object.keys(this.order.sharePoint.certificateId).map((key) =>
        Number(key)
      );
    }

    return arr;
  }

  get isDesignOrInstallationType(): boolean {
    return [
      InspectionTypeEnum.InspectionDesign,
      InspectionTypeEnum.InspectionInstallation,
      InspectionTypeEnum.InspectionDesignAndInstallation
    ].includes(this.data.inspection.inspectionType);
  }

  get isWaterNetworkType(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionWaterNetworkCharacterization
    );
  }

  get isGasExtinguishingSystemType(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionGasExtinguishingSystem
    );
  }

  get isFireTapsType(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionFireTaps
    );
  }

  get isFireExtinguishersType(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionFireExtinguishers
    );
  }

  get isAerosolShutdownType(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionAerosolShutdown
    );
  }

  get isFireAlarmType(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionFireAlarmSystem
    );
  }

  get isAirConditioninType(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionAirConditioningAndVentilationSystems
    );
  }

  get isVentilationSystemAndFireProtectionType(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionVentilationSystemAndFireProtection
    );
  }

  get IsPressureSystemInStairwellsCompatibilityCheckType(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionPressureSystemInStairwellsCompatibilityCheck
    );
  }

  get isIntegration(): boolean {
    return (
      this.data.inspection.inspectionType ===
      InspectionTypeEnum.InspectionIntegration
    );
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InvoiceData & { readonly: boolean },
    public dialogRef: MatDialogRef<void>,
    private readonly ordersService: OrdersService,
    private readonly message: NzMessageService,
    private readonly filesService: FilesService,
    private readonly usersService: UsersService,
    private readonly modal: NzModalService,
    private readonly translocoService: TranslocoService,
    private readonly common: CommonService
  ) {
    super();

    this.order = data.order;

    this.assignee = this.usersService.users.find(
      (user) => user.username === this.order.assignee
    );
    this.approval = this.usersService.users.find(
      (user) => user.username === this.order.approvedBy
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  public unloadNotification($event: { returnValue: boolean }) {
    if (!this.canDeactivate()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    return !this.isDirty;
  }

  createPDF(): void {
    this.submitting = true;

    let waitForFunc: string;
    if (
      this.order.inspectionType === InspectionTypeEnum.InspectionIntegration
    ) {
      waitForFunc = 'window.pageLoaded === true';
    }

    this.ordersService
      .createPDF(this.order.id, this.version, waitForFunc)
      .subscribe((resp) => this.onSuccess(resp), this.onError);
  }

  createFixedPDF(): void {
    this.version = this.order.version || 1;

    if (this.versions.length > 0) {
      this.modal.create({
        nzTitle: 'בחירת מספר גרסא',
        nzContent: this.orderVersionTemplate,
        nzOnOk: () => {
          this.createPDF();
        }
      });
    } else {
      this.createPDF();
    }
  }

  openPDF(): void {
    this.filesService.downloadFile({
      fileId: this.order.sharePoint?.certificateId?.[this.order.version],
      openInNewTab: true
    });
  }

  close(): void {
    if (this.isDirty) {
      this.modal.confirm({
        nzTitle: this.translocoService.translate('dialogs.confirm.exit.title'),
        nzContent: this.translocoService.translate(
          'dialogs.confirm.exit.content'
        ),
        nzOnOk: () => this.dialogRef.close()
      });
    } else {
      this.dialogRef.close();
    }
  }

  hide(): void {
    this.dialogRef.addPanelClass('hidden');
  }

  beforeCertificateUpload = (file: NzUploadFile): boolean => {
    let errorMsg: string;
    const isMaxSizeLimit = file.size > LIMIT.MAX_UPLOAD_SIZE;
    if (isMaxSizeLimit) {
      errorMsg = `גודל הקובץ צריך להיות קטן מ 100MB`;
    }

    if (errorMsg) {
      this.message.error(errorMsg);
    } else {
      const uploadDirectory = new UploadDirectory({
        namePath: 'certificats',
        uploadEntity: { id: this.order.id, type: Path.Orders },
        id: this.order.sharePoint?.certificats
      });

      const uploadFile = new UploadFile(file, uploadDirectory);
      this.filesService.upload([uploadFile]);

      uploadFile.uploadDestroyed$.subscribe(() => {
        if (file?.response?.id) {
          this.handleChange(file);
        }
      });
    }

    return false;
  };

  handleChange(file: NzUploadFile): void {
    switch (file.status) {
      case 'uploading':
        this.uploadingCertificate = true;
        break;
      case 'done':
        this.uploadingCertificate = false;
        break;
      case 'error':
        this.message.error('העלאת תעודה נכשלה');
        this.uploadingCertificate = false;
        break;
    }
  }

  deleteCertificate(): void {
    const subTitle = 'האם את/ה בטוח/ה שברצונך למחוק את התעודה?';

    this.modal.create({
      nzClosable: false,
      nzContent: NzModalConfirmActionComponent,
      nzComponentParams: {
        subTitle
      },
      nzOnOk: () => {
        this.ordersService.deleteCertificate(this.order.id).subscribe({
          next: () => {
            this.order.sharePoint.certificateId = {};
            this.message.success('התעודה נמחקה בהצלחה');
          },
          error: () => this.message.error('מחיקת התעודה נכשלה')
        });
      }
    });
  }

  private onSuccess = (file: FileItem) => {
    this.submitting = false;
    this.order.version = this.version;
    this.order.sharePoint = this.order.sharePoint || {};
    this.order.sharePoint.certificateId =
      this.order.sharePoint.certificateId || {};
    this.order.sharePoint.certificateId[this.version] = file.id;
    this.common.viewFile(file.id);
  };

  private onError = (error: unknown) => {
    this.submitting = false;
    console.error(error);
    this.message.create('error', 'הפעולה נכשלה', {
      nzDuration: 10000
    });
  };
}
